import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { ShoppingCart, Upload, RefreshCw, ExternalLink, CheckCircle2, Lock, Database, AlertTriangle, TrendingUp, X } from "lucide-react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState, Badge, DemoTag, StatCard } from "@/components/kit";

export default function Purchasing() {
  const [lists, setLists] = useState(null);
  const [sysco, setSysco] = useState(null);
  const [summary, setSummary] = useState(null);
  const [unmatched, setUnmatched] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/purchasing").then(({ data }) => setLists(data.purchasing || [])).catch(() => setLists([]));
    api.get("/suppliers/status").then(({ data }) => setSysco(data.sysco)).catch(() => {});
    api.get("/purchasing/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/suppliers/unmatched").then(({ data }) => setUnmatched(data)).catch(() => {});
    api.get("/price-alerts").then(({ data }) => setAlerts(data.alerts || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const ackAlert = async (id) => {
    try { await api.post(`/price-alerts/${id}/ack`); setAlerts((a) => a.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/suppliers/order-guide/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const loadSample = async () => {
    setBusy(true);
    try { const { data } = await api.post("/suppliers/order-guide/sample"); toast.success(data.message); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const orderDraft = async (id) => {
    try {
      const { data } = await api.post(`/purchasing/${id}/order-draft`);
      toast.success(data.message);
      if (data.sysco_url) window.open(data.sysco_url, "_blank", "noopener");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.post(`/purchasing/${id}/order-status`, { status }); toast.success(`Order marked ${status}.`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!lists) return <Loader />;
  const connected = sysco?.connected;

  return (
    <div>
      <PageHeader eyebrow="Procurement" title="Purchasing" subtitle="Pack-size aware lists priced from your real Sysco Order Guide." />

      {/* Supplier Connections */}
      <div className="matte-card p-6 mb-6">
        <p className="label-eyebrow mb-4 flex items-center gap-2"><Database size={14} className="text-gold" /> Supplier Connections</p>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-serif text-2xl">Sysco</span>
              {connected ? <Badge tone="green"><CheckCircle2 size={13} className="mr-1.5" /> Order Guide Connected</Badge> : <Badge tone="muted">Not connected</Badge>}
            </div>
            {connected && (
              <p className="text-white/45 text-sm mt-1.5">
                Last pricing update: {new Date(sysco.last_update).toLocaleDateString()} · <span className="text-gold font-mono">{sysco.product_count}</span> products mapped
                {sysco.account ? ` · ${sysco.account}` : ""}
              </p>
            )}
            {!connected && <p className="text-white/45 text-sm mt-1.5">Import your Order Guide (CSV/XLSX) to price PCA with real account costs.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" hidden onChange={importFile} data-testid="order-guide-file" />
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-guide-btn" className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <Upload size={14} /> {connected ? "Update Pricing" : "Import Order Guide"}
            </button>
            {!connected && <button onClick={loadSample} disabled={busy} data-testid="sample-guide-btn" className="btn-outline-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><RefreshCw size={14} /> Load Sample</button>}
            <a href={sysco?.shop_url || "https://shop.sysco.com/"} target="_blank" rel="noopener noreferrer" className="btn-outline-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><ExternalLink size={14} /> Open Sysco Shop</a>
          </div>
        </div>
      </div>

      {summary && (summary.drafts.count + summary.submitted.count + summary.received.count) > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Open Drafts" value={summary.drafts.count} sub={`$${summary.drafts.spend.toLocaleString()}`} />
          <StatCard label="Submitted" value={summary.submitted.count} sub={`$${summary.submitted.spend.toLocaleString()}`} />
          <StatCard label="Received" value={summary.received.count} sub={`$${summary.received.spend.toLocaleString()}`} />
          <StatCard label="Spend To Date" value={`$${summary.total_spend.toLocaleString()}`} accent />
        </div>
      )}

      {alerts.length > 0 && (
        <div className="matte-card p-6 mb-6 border border-red-500/30" data-testid="price-alerts-panel">
          <p className="label-eyebrow mb-4 flex items-center gap-2"><TrendingUp size={14} className="text-red-400" /> Price Change Alerts ({alerts.length})</p>
          <p className="text-white/45 text-sm mb-4">These dishes now exceed their target food cost after the latest pricing update. Re-price the dish, adjust the menu price, or optimize ingredients.</p>
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`price-alert-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-sm bg-red-500/5 border border-red-500/15">
                <div className="flex items-start gap-3 min-w-0">
                  <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <Link to={`/menus/${a.menu_id}`} className="text-white/90 hover:text-gold">{a.course}</Link>
                    <p className="text-white/40 text-xs mt-0.5">{a.menu_name}</p>
                    <p className="text-sm mt-1">
                      <span className="text-red-300 font-mono">{a.new_food_cost_pct}%</span>
                      <span className="text-white/40"> food cost vs </span>
                      <span className="text-white/70 font-mono">{a.target}%</span>
                      <span className="text-white/40"> target · plate cost </span>
                      <span className="font-mono text-white/60">${a.old_cost} → </span>
                      <span className="font-mono text-red-300">${a.new_cost}</span>
                    </p>
                  </div>
                </div>
                <button onClick={() => ackAlert(a.id)} data-testid={`ack-alert-${a.id}`} className="text-white/40 hover:text-white flex items-center gap-1.5 text-xs uppercase tracking-widest shrink-0">
                  <X size={13} /> Dismiss
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {unmatched?.imported && unmatched.total > 0 && (
        <div className="matte-card p-6 mb-6" data-testid="unmatched-report">
          <p className="label-eyebrow mb-1 flex items-center gap-2"><AlertTriangle size={14} className="text-gold" /> Unmatched Ingredients ({unmatched.total})</p>
          <p className="text-white/45 text-sm mb-4">These recipe ingredients aren't matched to a SKU in your Order Guide yet, so their cost is still estimated. Add them to your Sysco guide or check the naming to complete pricing.</p>
          <div className="space-y-2">
            {unmatched.menus.map((m) => (
              <div key={m.menu_id} className="rounded-sm border border-white/10 overflow-hidden">
                <button onClick={() => setOpen({ ...open, [`u-${m.menu_id}`]: !open[`u-${m.menu_id}`] })} data-testid={`unmatched-menu-${m.menu_id}`}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5">
                  <span className="text-white/85">{m.menu_name}</span>
                  <Badge tone="muted">{m.count} unmatched</Badge>
                </button>
                {open[`u-${m.menu_id}`] && (
                  <div className="px-4 py-3 border-t border-white/10 space-y-2">
                    {m.courses.map((c, i) => (
                      <div key={i} className="text-sm">
                        <p className="text-gold/80 text-xs uppercase tracking-wider">{c.course}</p>
                        <p className="text-white/60">{c.ingredients.join(", ")}</p>
                      </div>
                    ))}
                    <Link to={`/menus/${m.menu_id}`} className="text-gold text-sm hover:underline inline-flex items-center gap-1 mt-1">Open menu <ExternalLink size={12} /></Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {unmatched?.imported && unmatched.total === 0 && (
        <div className="matte-card p-4 mb-6 flex items-center gap-2 text-sm text-green-300" data-testid="unmatched-clear">
          <CheckCircle2 size={16} /> Every recipe ingredient is matched to your Order Guide — pricing is complete.
        </div>
      )}

      {lists.length === 0 ? <EmptyState icon={ShoppingCart} title="No purchasing lists" body="Open a menu and build a purchasing list to see it here." /> : (
        <div className="space-y-4">
          {lists.map((l) => (
            <div key={l.id} className="matte-card p-6" data-testid={`purchasing-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-serif text-2xl">{l.menu_name}</h3>
                  <p className="text-white/45 text-sm mt-1">{l.lines.length} items · {l.guests} guests · <span className="font-mono text-gold">${l.total_cost.toLocaleString()}</span> estimated</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpen({ ...open, [l.id]: !open[l.id] })} className="btn-outline-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm">{open[l.id] ? "Hide" : "Review Order"}</button>
                  {l.in_cart
                    ? <OrderTracker status={l.order_status || "draft"} onSet={(s) => setStatus(l.id, s)} id={l.id} />
                    : connected
                      ? <button onClick={() => orderDraft(l.id)} data-testid={`order-draft-${l.id}`} className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><ShoppingCart size={13} /> Create Sysco Order Draft</button>
                      : <span className="flex items-center gap-1.5 text-white/40 text-xs"><Lock size={13} /> Import guide to order</span>}
                </div>
              </div>
              {open[l.id] && (
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left label-eyebrow border-b border-white/10">
                      <th className="py-2 pr-3">Ingredient</th><th className="py-2 px-3">SKU</th>
                      <th className="py-2 px-3 text-right">Required</th><th className="py-2 px-3 text-right">On Hand</th>
                      <th className="py-2 px-3 text-right">To Purchase</th><th className="py-2 px-3 text-right">Remainder</th><th className="py-2 pl-3 text-right">Est. Cost</th>
                    </tr></thead>
                    <tbody className="font-mono">
                      {l.lines.map((r, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-black/40">
                          <td className="py-2.5 pr-3 font-sans text-white/85">{r.name}</td>
                          <td className="py-2.5 px-3 text-white/40 text-xs">{r.sku}</td>
                          <td className="py-2.5 px-3 text-right text-white/60">{r.required} {r.unit}</td>
                          <td className="py-2.5 px-3 text-right text-white/60">{r.on_hand} {r.unit}</td>
                          <td className="py-2.5 px-3 text-right text-gold">{r.to_purchase} {r.purchase_unit}</td>
                          <td className="py-2.5 px-3 text-right text-white/40">{r.remainder} {r.unit}</td>
                          <td className="py-2.5 pl-3 text-right text-white/70">${r.estimated_cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-white/30 text-xs">{l.note}</p>
                    <a href="https://shop.sysco.com/" target="_blank" rel="noopener noreferrer" className="text-gold text-sm hover:underline flex items-center gap-1">Continue in Sysco Shop <ExternalLink size={13} /></a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STEPS = ["draft", "submitted", "received"];
const STEP_LABEL = { draft: "Draft", submitted: "Submitted", received: "Received" };

function OrderTracker({ status, onSet, id }) {
  const idx = Math.max(0, STEPS.indexOf(status));
  const next = STEPS[idx + 1];
  return (
    <div className="flex items-center gap-3" data-testid={`order-tracker-${id}`}>
      <div className="flex items-center gap-1.5">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`flex items-center gap-1 text-xs ${i <= idx ? "text-gold" : "text-white/30"}`}>
              <span className={`h-2 w-2 rounded-full ${i <= idx ? "bg-gold" : "bg-white/20"}`} />{STEP_LABEL[s]}
            </span>
            {i < STEPS.length - 1 && <span className={`h-px w-4 ${i < idx ? "bg-gold" : "bg-white/15"}`} />}
          </React.Fragment>
        ))}
      </div>
      {next
        ? <button onClick={() => onSet(next)} data-testid={`mark-${next}-${id}`} className="btn-outline-gold px-4 py-2 text-xs uppercase tracking-widest rounded-sm">Mark {STEP_LABEL[next]}</button>
        : <Badge tone="green"><CheckCircle2 size={13} className="mr-1.5" /> Received</Badge>}
    </div>
  );
}
